import LabelLinked from '@/components/form/LabelLinked'
import fetchApi from '@/lib/tools/api'
import {normalizeString} from '@/lib/tools/utils'
import {ProductTerroir, Terroir} from '@/types'
import ContentSave from '@mui/icons-material/Save'
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography
} from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import React, {Fragment, SyntheticEvent, useCallback, useEffect, useMemo, useState} from 'react'
import {useNotify, useSaveContext} from 'react-admin'
import useOnEvent from 'react-app-events/lib/useOnEvent'
import {RiCloseCircleFill} from 'react-icons/ri'
import TrustIndicator from '../layout/TrustIndicator'

const PARAMS = {
	context: 'terroir',
	endpoints: {
		search: () => `/terroirs`,
		getLinked: (id: number) => `/products/${id}/terroirs`,
		save: (id: number) => `/products/${id}/terroirs`,
		add: (id: number) => `/products/${id}/terroirs/add`,
		delete: (id: number) => `/products/${id}/terroirs/remove`
	}
}

export default function ProductTerroirs({productId}: { productId: number }) {

	const notify = useNotify()
	const {saving} = useSaveContext()

	const [filter, setFilter] = useState('')
	const [options, setOptions] = useState<Terroir[]>([])
	const [touched, setTouched] = useState<boolean>(false)

	const [linkedOptions, setLinkedOptions] = useState<ProductTerroir[]>([])

	const [loading, setLoading] = useState(false)
	const [loadingSearch, setLoadingSearch] = useState(false)
	const [open, setOpen] = useState(false)

	/**
	 * Event
	 * Find linked product's terroir
	 */
	useOnEvent('PRODUCTS_COMMUNES_SAVED', () => {
		handleGetLinked()
	})

	useEffect(() => {
		if (saving)
			handleSave()
	}, [saving])

	/**
	 * Event
	 * Find linked product's terroir
	 */
	useEffect(() => {
		handleGetLinked()
	}, [])

	/**
	 * Methods
	 * Search Linked Terroirs
	 */
	const handleGetLinked = useCallback(async () => {
		setLoading(true)

		/**
		 * Liaisons
		 */
		await fetchApi<ProductTerroir[]>(
			PARAMS.endpoints.getLinked(productId)
		)
			.then((res: ProductTerroir[]) => {
				setLinkedOptions(res ?
					res.sort((a: ProductTerroir, b: ProductTerroir) => a.terroir.name.localeCompare(b.terroir.name))
					: []
				)
			})

		setLoadingSearch(false)
		setLoading(false)
	}, [productId])

	/**
	 * Methods
	 * Search options
	 */
	const handleSearch = useCallback(async (event: SyntheticEvent<Element, Event>, value: string) => {

		// @todo throttle request
		setLoadingSearch(true)

		/**
		 * Terroirs
		 */
		const results = await fetchApi<Terroir[]>(
			PARAMS.endpoints.search(),
			undefined,
			{q: value}
		)

		const resultsSorted = results.sort((a, b) => a.name.localeCompare(b.name))

		setOptions(resultsSorted)
		setLoadingSearch(false)
	}, [])

	/**
	 * Methods
	 * Save/Add/Remove row
	 */

	const updateLinked = useCallback((id: number, key: string, value: boolean | number) => {
		setLinkedOptions((linked) => {
			return linked.map((row) => {
				return row.id === id ? {
					...row,
					...{
						[key]: value
					}
				} : row
			})
		})

		setTouched(true)
	}, [])

	/**
	 * Methods
	 * Save/Add/Remove row
	 */

	const handleSelect = useCallback((event: SyntheticEvent, item: Terroir | null) => {

		if (!item) return

		setLoadingSearch(true)

		fetchApi<ProductTerroir>(
			PARAMS.endpoints.add(productId),
			{id: item.id},
			undefined,
			'PUT'
		)
			.then((res: ProductTerroir) => {
				notify('Liaison ajoutée', {
					type: 'info',
					messageArgs: {smart_count: 1}
				})
				setLoadingSearch(false)
				setLinkedOptions((linked) => {
					return [
						...linked,
						...[res]
					]
				})
				setFilter(`${res.terroir.code} -- ${res.terroir.name}`)
			})

	}, [])

	const handleSave = useCallback(() => {

		fetchApi(
			PARAMS.endpoints.save(productId),
			{linkedOptions},
			undefined,
			'PUT'
		)
			.then(() => {
				setTouched(false)
				notify('Liaisons sauvegardées', {
					type: 'info',
					messageArgs: {smart_count: 1}
				})
			})
		setFilter('')

	}, [linkedOptions])

	const handleRemove = useCallback((item: ProductTerroir) => {
		if (confirm('Supprimer la liaison ?')) {
			fetchApi(
				PARAMS.endpoints.delete(productId),
				{id: item.id},
				undefined,
				'PUT'
			)
				.then(res => {
					if (res) {
						setLinkedOptions((linked) =>
							linked.filter(t => t.id !== item.id)
						)
						notify('Liaison supprimée', {
							type: 'info',
							messageArgs: {smart_count: 1}
						})
					}
				})
		}
	}, [])

	const linkedOptionsFiltered = useMemo(() => linkedOptions.filter((item: ProductTerroir) =>
		normalizeString(item.terroir.code + ' -- ' + item.terroir.name).indexOf(normalizeString(filter)) >= 0
	), [filter, linkedOptions])

	return (
		<Box>
			<Typography variant="h6" gutterBottom>Terroirs liés</Typography>
			<Box sx={{
				display: 'flex',
				justifyContent: 'space-between',
				marginBottom: '16px',
				gap: '12px'
			}}>
				{linkedOptions.length > 0 &&
                    <TextField
                        value={filter}
                        variant="outlined"
                        label="Rechercher une liaison"
                        sx={{flex: '1 0 50%'}}
                        onChange={({target: {value}}) => setFilter(value)}
                    />
				}
				<Autocomplete
					id="search_terroirs"
					noOptionsText={'Aucun résultat'}
					renderInput={(params) =>
						<TextField variant="outlined" {...params} label="Ajouter une liaison" InputProps={{
							...params.InputProps,
							endAdornment: (
								<Fragment>
									{loadingSearch ?
										<CircularProgress sx={{marginRight: '30px'}} color="inherit" disableShrink
														  size={20}/> : null}
									{params.InputProps.endAdornment}
								</Fragment>
							)
						}}/>
					}
					getOptionDisabled={(option) => !!linkedOptions.find(t => t.terroir.id === option.id)}
					sx={{flex: '1 0 50%'}}
					options={options}
					getOptionLabel={(option: Terroir) => `${option.code} -- ${option.name}`}
					open={open}
					onOpen={() => setOpen(true)}
					onClose={() => setOpen(false)}
					filterOptions={(options) => options.map(option => {
						option.linked = linkedOptions.find(t => t.terroir.id === option.id)
						return option
					})}
					onInputChange={handleSearch}
					onChange={handleSelect}
				/>
			</Box>
			<Box sx={{display: 'flex', gap: '16px', flexDirection: 'column', alignItems: 'flex-start'}}>
				<Paper
					sx={{
						width: '100%',
						// minHeight: 300,
						overflow: 'hidden',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center'
					}}
				>
					{loading && <CircularProgress disableShrink size="5rem"/>}
					{!loading &&
                        <TableContainer
							// sx={{height: 300}}
                        >
                            <Table stickyHeader={true} aria-label="sticky table" size="small" sx={{
								height: 'max-content',
								border: 'black',
								fontSize: {
									md: 'inherit',
									xs: '13px'
								}
							}}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{width: 300}}> Libellé </TableCell>
                                        <TableCell>Est 1932</TableCell>
                                        <TableCell>Est 1992</TableCell>
                                        <TableCell>Est 2022</TableCell>
                                        <TableCell>Indice de confiance</TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
									{linkedOptionsFiltered.map((item: ProductTerroir) => (
										<TableRow key={item.id}>
											<TableCell>
												{`${item.terroir.code} -- `}
												<LabelLinked resource="terroirs" label={`${item.terroir.name}`}
															 id={item.terroir.id}/>
											</TableCell>
											<TableCell>
												<Checkbox
													id={item.id + '_1932'}
													onChange={({target}) => updateLinked(item.id, 'is1932', target.checked)}
													checked={item.is1932}
												/>
											</TableCell>
											<TableCell>
												<Checkbox
													id={item.id + '_1992'}
													onChange={({target}) => updateLinked(item.id, 'is1992', target.checked)}
													checked={item.is1992}
												/>
											</TableCell>
											<TableCell>
												<Checkbox
													id={item.id + '_2022'}
													onChange={({target}) => updateLinked(item.id, 'is2022', target.checked)}
													checked={item.is2022}
												/>
											</TableCell>
											<TableCell>
												<TrustIndicator
													value={item.trust}
													onChange={(event: Event, value: number) => updateLinked(item.id, 'trust', value)}
												/>
											</TableCell>
											<TableCell>
												<IconButton sx={{}} onClick={() => handleRemove(item)}>
													<RiCloseCircleFill/>
												</IconButton>
											</TableCell>
										</TableRow>
									))}
                                </TableBody>
                            </Table>
                        </TableContainer>
					}
				</Paper>
				<Button disabled={!touched} variant="contained" sx={{gap: '8px'}} onClick={handleSave}><ContentSave
					height={18} width={18}/>Enregistrer</Button>
			</Box>
		</Box>
	)
}
